import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { useToast } from '../../components/Toast';

interface ManualInfo {
  id: string;
  title: string;
  exists: boolean;
  size: number;
  file: string;
}

const EducationManagement: React.FC<{ admin: any; onLogout: () => void }> = ({ admin, onLogout }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [manuals, setManuals] = useState<ManualInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [eduDays, setEduDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const fetchManuals = async () => {
    try {
      const [docs, expiry] = await Promise.all([
        adminApi.getEducationManuals(),
        adminApi.getEduExpiry(),
      ]);
      setManuals(docs);
      setEduDays(expiry.edu_expiry_days);
    } catch {
      toast.error('교육 자료 정보를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchManuals(); }, []);

  const handleSaveExpiry = async () => {
    setSaving(true);
    try {
      await adminApi.setEduExpiry(eduDays);
      toast.success('만료 기간이 저장되었습니다.');
    } catch {
      toast.error('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (docId: string, file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('PDF 파일만 업로드 가능합니다.');
      return;
    }
    setUploading(docId);
    try {
      await adminApi.uploadEducationManual(docId, file);
      toast.success('파일이 교체되었습니다.');
      fetchManuals();
    } catch {
      toast.error('업로드에 실패했습니다.');
    } finally {
      setUploading(null);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const token = localStorage.getItem('hire_admin_token');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* 헤더 */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="text-slate-400 hover:text-slate-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <img src="/grk-logo.jpg" alt="GRK" className="h-7 object-contain" style={{ mixBlendMode: 'multiply' }} />
            <span className="text-sm font-semibold text-slate-800">교육 자료 관리</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{admin?.name}</span>
            <button onClick={onLogout} className="text-sm text-slate-400 hover:text-slate-600">로그아웃</button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 만료 기간 설정 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="text-sm font-semibold text-slate-800 mb-4">열람 링크 만료 설정</h2>
          <div className="flex items-center gap-3">
            <input
              type="number" min={1} max={365} value={eduDays}
              onChange={(e) => setEduDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
              className="w-20 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span className="text-sm text-slate-600">일 후 만료</span>
            <button
              onClick={handleSaveExpiry} disabled={saving}
              className="ml-4 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-2">지원자에게 발송하는 교육 자료 링크가 설정한 기간 후 만료됩니다.</p>
        </div>

        {/* 교육 자료 목록 */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-800">교육 자료 (PDF)</h2>
            <p className="text-xs text-slate-400 mt-1">파일을 교체하면 기존 파일은 백업됩니다.</p>
          </div>

          <div className="divide-y divide-slate-100">
            {manuals.map((m) => (
              <div key={m.id} className="px-6 py-5 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800">{m.title}</span>
                    {m.exists ? (
                      <span className="text-xs px-2 py-0.5 bg-green-50 text-green-600 rounded-full font-medium">
                        {formatSize(m.size)}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 bg-red-50 text-red-500 rounded-full font-medium">
                        미등록
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{m.file}</p>
                </div>

                <div className="flex items-center gap-2">
                  {/* 미리보기 */}
                  {m.exists && (
                    <button
                      onClick={() => setPreviewDoc(previewDoc === m.id ? null : m.id)}
                      className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                    >
                      {previewDoc === m.id ? '닫기' : '미리보기'}
                    </button>
                  )}

                  {/* 파일 교체 */}
                  <input
                    type="file"
                    accept=".pdf"
                    ref={(el) => { fileInputRefs.current[m.id] = el; }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUpload(m.id, file);
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRefs.current[m.id]?.click()}
                    disabled={uploading === m.id}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-slate-600 rounded-lg hover:bg-slate-700 disabled:opacity-50"
                  >
                    {uploading === m.id ? '업로드 중...' : m.exists ? '파일 교체' : '파일 등록'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PDF 미리보기 */}
        {previewDoc && (
          <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">
                {manuals.find((m) => m.id === previewDoc)?.title} 미리보기
              </span>
              <button onClick={() => setPreviewDoc(null)} className="text-xs text-slate-400 hover:text-slate-600">
                닫기
              </button>
            </div>
            <iframe
              src={`/api/admin/education-manuals/${previewDoc}/pdf`}
              className="w-full border-0"
              style={{ height: '80vh' }}
              title="PDF Preview"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EducationManagement;
